import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";


const FloorsSelector = ({
  floors,
  buildingType,
  onChange,
  onContinue,
  onBack,
}) => {
  const [floorCount, setFloorCount] = useState(floors || 2);
  const [isValid, setIsValid] = useState(true);
  const [buildingImage, setBuildingImage] = useState(
    "/images/products/default-building.jpg"
  );
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (floors) {
      setFloorCount(floors);
    }
  }, []);

  useEffect(() => {
    if (floors && floors !== floorCount) {
      setFloorCount(floors);
    }
  }, [floors]);

  // Define responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth <= 1024 && windowWidth > 768;
  const isSmallMobile = windowWidth <= 480;

  // Minimum and maximum floor recommendations based on building type
  const floorRanges = {
    residential: { min: 2, max: 10 },
    commercial: { min: 2, max: 10 },
    hospital: { min: 2, max: 10 },
    hotel: { min: 2, max: 10 },
    factory: { min: 2, max: 10 },
    other: { min: 2, max: 15 },
  };

  const currentRange = floorRanges[buildingType] || floorRanges.other;

  // Load appropriate building image
  const buildingImages = {
    residential: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753253374/residential-building_v2t9yi.jpg",
    commercial: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753253374/commercial-building_ewvygc.jpg",
    hospital: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753253374/hospital-building_otxcuc.jpg",
    hotel: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753253376/hotel-building_v96lfu.png",
    factory: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753253376/industrial-building_ucgjb4.png",
    other: "https://res.cloudinary.com/dknsjvavr/image/upload/v1753253376/other-building_gnrcxz.png",
  };

  useEffect(() => {
    setBuildingImage(buildingImages[buildingType] || buildingImages.other);
  }, [buildingType]);

  // Update parent state when local state changes
  useEffect(() => {
    // Only call onChange if this isn't just responding to a prop change
    if (floors !== floorCount) {
      onChange(floorCount);
    }

    // The validation can stay the same
    setIsValid(
      floorCount >= currentRange.min && floorCount <= currentRange.max
    );
  }, [floorCount, onChange, currentRange, floors]);

  // Handle manual input
  const handleInputChange = (e) => {
    const value = parseInt(e.target.value) || currentRange.min;
    setFloorCount(
      Math.min(Math.max(value, currentRange.min), currentRange.max)
    );
  };

  // Handle increment/decrement
  const adjustFloors = (increment) => {
    setFloorCount((prev) => {
      const newValue = prev + increment;
      return Math.min(Math.max(newValue, currentRange.min), currentRange.max);
    });
  };

  const buildingTypeCapitalized =
    buildingType.charAt(0).toUpperCase() + buildingType.slice(1);

  // Get responsive styles based on screen width
  const getResponsiveStyles = () => {
    return {
      container: {
        padding: isSmallMobile ? "10px 0 20px" : "20px 0 40px",
      },
      selectorLayout: {
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? "30px" : "40px",
        maxWidth: "1100px",
        margin: "0 auto",
      },
      imageContainer: {
        minHeight: isSmallMobile ? "250px" : isMobile ? "350px" : "450px",
      },
      sectionTitle: {
        fontSize: isSmallMobile ? "24px" : isMobile ? "28px" : "32px",
        marginBottom: isMobile ? "10px" : "15px",
      },
      sectionDescription: {
        fontSize: isSmallMobile ? "15px" : isMobile ? "16px" : "17px",
        marginBottom: isMobile ? "20px" : "30px",
      },
      floorInput: {
        width: isSmallMobile ? "80px" : isMobile ? "100px" : "120px",
        height: isSmallMobile ? "50px" : isMobile ? "60px" : "70px",
        fontSize: isSmallMobile ? "24px" : isMobile ? "28px" : "34px",
      },
      floorCountNumber: {
        fontSize: isSmallMobile ? "48px" : isMobile ? "56px" : "64px",
      },
      floorCountText: {
        fontSize: isSmallMobile ? "16px" : isMobile ? "18px" : "20px",
      },
      floorIndicator: {
        width: isSmallMobile ? "50px" : isMobile ? "60px" : "80px",
      },
      controlButton: {
        width: isSmallMobile ? "44px" : isMobile ? "48px" : "54px",
        height: isSmallMobile ? "44px" : isMobile ? "48px" : "54px",
      },
      navigationContainer: {
        flexDirection: isSmallMobile ? "column" : "row",
        gap: isSmallMobile ? "15px" : "0",
        maxWidth: "1100px",
        margin: isSmallMobile ? "25px auto 0" : "40px auto 0",
        padding: isMobile ? "0 15px" : "0",
      },
      navigationButton: {
        width: isSmallMobile ? "100%" : "auto",
        justifyContent: isSmallMobile ? "center" : "flex-start",
      },
      recommendationBox: {
        padding: isSmallMobile ? "16px" : "24px",
        marginTop: isSmallMobile ? "20px" : "30px",
      },
      recommendationContent: {
        flexDirection: isSmallMobile ? "column" : "row",
        gap: isSmallMobile ? "15px" : "0",
      },
      recommendationDivider: {
        width: isSmallMobile ? "100%" : "1px",
        height: isSmallMobile ? "1px" : "50px",
        margin: isSmallMobile ? "5px 0" : "0",
      },
    };
  };

  const responsiveStyles = getResponsiveStyles();

  return (
    <div style={{ ...styles.container, ...responsiveStyles.container }}>
      <div
        style={{ ...styles.selectorLayout, ...responsiveStyles.selectorLayout }}
      >
        <div style={styles.visualSection}>
          <motion.div
            style={{
              ...styles.imageContainer,
              ...responsiveStyles.imageContainer,
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <img
              src={buildingImage}
              alt={`${buildingType} building with ${floorCount} floors`}
              style={styles.buildingImage}
            />
            <div style={styles.floorVisualizer}>
              <AnimatePresence>
                {Array.from({ length: Math.min(15, floorCount) }, (_, i) => (
                  <motion.div
                    key={i}
                    style={{
                      ...styles.floorIndicator,
                      ...responsiveStyles.floorIndicator,
                      opacity: i / 15 + 0.4,
                    }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: i / 15 + 0.4, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ delay: i * 0.03 }}
                  />
                ))}
              </AnimatePresence>
              {floorCount > 15 && (
                <motion.div
                  style={styles.moreFloors}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  +{floorCount - 15} more
                </motion.div>
              )}
            </div>

            <div style={styles.imageOverlay} />

            <div style={styles.floorCount}>
              <span
                style={{
                  ...styles.floorCountNumber,
                  ...responsiveStyles.floorCountNumber,
                }}
              >
                {floorCount}
              </span>
              <span
                style={{
                  ...styles.floorCountText,
                  ...responsiveStyles.floorCountText,
                }}
              >
                Floors
              </span>
            </div>
          </motion.div>
        </div>

        <div style={styles.controlSection}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h3
              style={{
                ...styles.sectionTitle,
                ...responsiveStyles.sectionTitle,
              }}
            >
              Configure Floor Count
            </h3>
            <p
              style={{
                ...styles.sectionDescription,
                ...responsiveStyles.sectionDescription,
              }}
            >
              For{" "}
              <span style={styles.highlight}>
                {buildingTypeCapitalized} buildings
              </span>
              , we typically recommend
              <span style={styles.highlight}>
                {" "}
                {currentRange.recommended} floors
              </span>
              , but you can select between {currentRange.min} and{" "}
              {currentRange.max} depending on your specific requirements.
            </p>

            <div style={styles.controlGroup}>
              <motion.button
                style={{
                  ...styles.controlButton,
                  ...responsiveStyles.controlButton,
                  ...(floorCount <= currentRange.min
                    ? styles.disabledButton
                    : {}),
                }}
                whileHover={floorCount > currentRange.min ? { scale: 1.1 } : {}}
                whileTap={floorCount > currentRange.min ? { scale: 0.95 } : {}}
                onClick={() => adjustFloors(-1)}
                disabled={floorCount <= currentRange.min}
                aria-label="Decrease floor count"
              >
                <i className="fas fa-minus" style={styles.controlIcon}></i>
              </motion.button>

              <div style={styles.inputContainer}>
                <input
                  type="number"
                  value={floorCount}
                  onChange={handleInputChange}
                  min={currentRange.min}
                  max={currentRange.max}
                  style={{
                    ...styles.floorInput,
                    ...responsiveStyles.floorInput,
                  }}
                  aria-label="Number of floors"
                />
                <span style={styles.inputLabel}>FLOORS</span>
              </div>

              <motion.button
                style={{
                  ...styles.controlButton,
                  ...responsiveStyles.controlButton,
                  ...(floorCount >= currentRange.max
                    ? styles.disabledButton
                    : {}),
                }}
                whileHover={floorCount < currentRange.max ? { scale: 1.1 } : {}}
                whileTap={floorCount < currentRange.max ? { scale: 0.95 } : {}}
                onClick={() => adjustFloors(1)}
                disabled={floorCount >= currentRange.max}
                aria-label="Increase floor count"
              >
                <i className="fas fa-plus" style={styles.controlIcon}></i>
              </motion.button>
            </div>

            {!isValid && (
              <motion.p
                style={styles.errorMessage}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <i
                  className="fas fa-exclamation-triangle"
                  style={styles.errorIcon}
                ></i>
                Please select between {currentRange.min} and {currentRange.max}{" "}
                floors.
              </motion.p>
            )}

            <motion.div
              style={{
                ...styles.recommendationBox,
                ...responsiveStyles.recommendationBox,
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div style={styles.recommendationHeader}>
                <i
                  className="fas fa-lightbulb"
                  style={styles.recommendationIcon}
                ></i>
                <h4 style={styles.recommendationTitle}>
                  Expert Recommendations
                </h4>
              </div>

              <div
                style={{
                  ...styles.recommendationContent,
                  ...responsiveStyles.recommendationContent,
                }}
              >
                <div style={styles.recommendationItem}>
                  <span style={styles.itemLabel}>Optimal</span>
                  <span style={styles.itemValue}>
                    {currentRange.recommended}
                  </span>
                  <span style={styles.itemUnit}>floors</span>
                </div>

                <div
                  style={{
                    ...styles.recommendationDivider,
                    ...responsiveStyles.recommendationDivider,
                  }}
                ></div>

                <div style={styles.recommendationItem}>
                  <span style={styles.itemLabel}>Range</span>
                  <span style={styles.itemValue}>
                    {currentRange.min}-{currentRange.max}
                  </span>
                  <span style={styles.itemUnit}>floors</span>
                </div>

                <div
                  style={{
                    ...styles.recommendationDivider,
                    ...responsiveStyles.recommendationDivider,
                  }}
                ></div>

                <div style={styles.recommendationItem}>
                  <span style={styles.itemLabel}>Building Type</span>
                  <span style={styles.itemValueSmall}>
                    {buildingTypeCapitalized}
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Navigation buttons */}
      <motion.div
        style={{
          ...styles.navigationContainer,
          ...responsiveStyles.navigationContainer,
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <motion.button
          onClick={onBack}
          style={{ ...styles.backButton, ...responsiveStyles.navigationButton }}
          whileHover={{ scale: 1.03, backgroundColor: "#f5f5f7" }}
          whileTap={{ scale: 0.98 }}
        >
          <i className="fas fa-chevron-left" style={styles.buttonIconLeft}></i>
          Back
        </motion.button>

        <motion.button
          style={{
            ...styles.continueButton,
            ...responsiveStyles.navigationButton,
          }}
          whileHover={{
            scale: 1.03,
            boxShadow: "0 8px 20px rgba(22, 59, 162, 0.3)",
          }}
          whileTap={{ scale: 0.98 }}
          onClick={onContinue}
        >
          Continue
          <i
            className="fas fa-chevron-right"
            style={styles.buttonIconRight}
          ></i>
        </motion.button>
      </motion.div>
    </div>
  );
};

const styles = {
  container: {
    padding: "20px 0 40px",
  },
  selectorLayout: {
    display: "flex",
    flexDirection: "row",
    gap: "40px",
    maxWidth: "1100px",
    margin: "0 auto",
    "@media (max-width: 768px)": {
      flexDirection: "column",
      gap: "30px",
    },
  },
  visualSection: {
    flex: "1 1 50%",
  },
  controlSection: {
    flex: "1 1 50%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  imageContainer: {
    position: "relative",
    borderRadius: "24px",
    overflow: "hidden",
    boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
    minHeight: "450px",
    background: "linear-gradient(45deg, #1a2a6c, #2a4858)",
    "@media (max-width: 768px)": {
      minHeight: "350px",
    },
  },
  buildingImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    opacity: 0.85,
  },
  imageOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 30%, rgba(0,0,0,0.1) 100%)",
    zIndex: 1,
  },
  floorCount: {
    position: "absolute",
    left: "30px",
    bottom: "30px",
    zIndex: 5,
    color: "white",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  floorCountNumber: {
    fontSize: "64px",
    fontWeight: "700",
    lineHeight: "1",
    textShadow: "0 2px 10px rgba(0,0,0,0.3)",
  },
  floorCountText: {
    fontSize: "20px",
    fontWeight: "500",
    opacity: 0.9,
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  floorVisualizer: {
    position: "absolute",
    right: "30px",
    bottom: "30px",
    top: "30px",
    display: "flex",
    flexDirection: "column-reverse",
    justifyContent: "flex-start",
    gap: "4px",
    zIndex: 2,
  },
  floorIndicator: {
    width: "80px",
    height: "6px",
    backgroundColor: "rgba(255,255,255,0.8)",
    borderRadius: "3px",
    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
  },
  moreFloors: {
    color: "white",
    backgroundColor: "rgba(22, 59, 162, 0.9)",
    borderRadius: "4px",
    padding: "6px 10px",
    fontSize: "12px",
    marginBottom: "8px",
    textAlign: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
    fontWeight: "500",
  },
  sectionTitle: {
    fontSize: "32px",
    fontWeight: "600",
    color: "#1d1d1f",
    marginBottom: "15px",
    "@media (max-width: 768px)": {
      fontSize: "28px",
    },
  },
  sectionDescription: {
    fontSize: "17px",
    lineHeight: "1.6",
    color: "#4d4d4d",
    marginBottom: "30px",
  },
  highlight: {
    color: "#163ba2",
    fontWeight: "600",
  },
  controlGroup: {
    display: "flex",
    alignItems: "center",
    marginBottom: "20px",
  },
  controlButton: {
    width: "54px",
    height: "54px",
    borderRadius: "50%",
    border: "none",
    backgroundColor: "#163ba2",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(22, 59, 162, 0.25)",
    outlineOffset: "3px",
  },
  controlIcon: {
    fontSize: "18px",
  },
  disabledButton: {
    backgroundColor: "#e0e0e0",
    cursor: "not-allowed",
    boxShadow: "none",
    color: "#a0a0a0",
  },
  inputContainer: {
    position: "relative",
    margin: "0 20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  floorInput: {
    width: "120px",
    height: "70px",
    fontSize: "34px",
    fontWeight: "600",
    textAlign: "center",
    border: "2px solid #e0e0e0",
    borderRadius: "12px",
    outline: "none",
    transition: "all 0.2s ease",
    color: "#1d1d1f",
    backgroundColor: "#f9f9f9",
    "&:focus": {
      borderColor: "#163ba2",
      boxShadow: "0 0 0 3px rgba(22, 59, 162, 0.2)",
    },
    "@media (max-width: 768px)": {
      fontSize: "28px",
      height: "60px",
      width: "100px",
    },
  },
  inputLabel: {
    marginTop: "8px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#86868b",
    letterSpacing: "1px",
  },
  errorMessage: {
    color: "#ff3b30",
    fontSize: "14px",
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    backgroundColor: "rgba(255, 59, 48, 0.1)",
    borderRadius: "8px",
  },
  errorIcon: {
    fontSize: "14px",
  },
  recommendationBox: {
    backgroundColor: "#f9f9fb",
    borderRadius: "16px",
    padding: "24px",
    marginTop: "30px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
    border: "1px solid #eaeaea",
  },
  recommendationHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
    gap: "10px",
  },
  recommendationIcon: {
    color: "#ff9500",
    fontSize: "20px",
  },
  recommendationTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1d1d1f",
    margin: "0",
  },
  recommendationContent: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
  },
  recommendationItem: {
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0 10px",
  },
  recommendationDivider: {
    width: "1px",
    height: "50px",
    backgroundColor: "#e0e0e0",
  },
  itemLabel: {
    fontSize: "13px",
    color: "#86868b",
    marginBottom: "6px",
    fontWeight: "500",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  itemValue: {
    fontSize: "26px",
    color: "#163ba2",
    fontWeight: "700",
  },
  itemValueSmall: {
    fontSize: "18px",
    color: "#163ba2",
    fontWeight: "600",
  },
  itemUnit: {
    fontSize: "12px",
    color: "#86868b",
    fontWeight: "500",
    marginTop: "2px",
  },
  navigationContainer: {
    display: "flex",
    justifyContent: "space-between",
    maxWidth: "1100px",
    margin: "40px auto 0",
    "@media (max-width: 768px)": {
      padding: "0 10px",
    },
  },
  backButton: {
    backgroundColor: "transparent",
    border: "1px solid #d2d2d7",
    borderRadius: "12px",
    padding: "14px 28px",
    color: "#1d1d1f",
    fontSize: "16px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s ease",
    outline: "none",
    "&:focus": {
      boxShadow: "0 0 0 3px rgba(0, 102, 204, 0.3)",
    },
  },
  buttonIconLeft: {
    marginRight: "10px",
    fontSize: "14px",
  },
  continueButton: {
    backgroundColor: "#163ba2",
    color: "white",
    border: "none",
    borderRadius: "12px",
    padding: "14px 28px",
    fontSize: "16px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(22, 59, 162, 0.25)",
    outline: "none",
    "&:focus": {
      boxShadow:
        "0 0 0 3px rgba(22, 59, 162, 0.3), 0 4px 12px rgba(22, 59, 162, 0.25)",
    },
  },
  buttonIconRight: {
    marginLeft: "10px",
    fontSize: "14px",
  },
};

export default FloorsSelector;
